import React, { useState, useEffect } from 'react';
import { 
  checkServerAvailability,  
  getServerInfo,
  ConnectionType,
  getDatastoreStats,
  getRDFoxURL
} from '../utils/rdfoxUtils';
import fetchRDFoxData from '../api/RDFoxRequest';
import { 
  Card, 
  Layout, 
  Row, 
  Col, 
  Button, 
  Form, 
  Alert, 
  Spin, 
  Table, 
  Input, 
  Select, 
  Typography, 
  Space, 
  Divider, 
  Tag
} from 'antd';
// import type { RadioChangeEvent } from 'antd';
import { 
  CloudServerOutlined, 
  DatabaseOutlined, 
  ReloadOutlined, 
  SearchOutlined 
} from '@ant-design/icons';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const RDFoxExplorer: React.FC = () => {
  // Utiliser uniquement la connexion directe
  const [connectionType] = useState<ConnectionType>('direct');
  
  // States to store server information
  const [serverStatus, setServerStatus] = useState<{
    available: boolean;
    datastores: string[];
  }>({ available: false, datastores: [] });
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [serverInfo, setServerInfo] = useState<any>(null);
  const [selectedDatastore, setSelectedDatastore] = useState<string>('');
  const [queryResults, setQueryResults] = useState<any>(null);
  const [sparqlQuery, setSparqlQuery] = useState<string>(`
SELECT ?s ?p ?o
WHERE {
  ?s ?p ?o
}
LIMIT 10
  `);
  const [queryLoading, setQueryLoading] = useState<boolean>(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [tripletCount, setTripletCount] = useState<number | null>(null);
  const [datastoreStats, setDatastoreStats] = useState<any>(null);

  // Charger les informations du serveur au chargement de la page
  useEffect(() => {
    checkServer();
  }, [connectionType]); // Recharger quand le type de connexion change

  // Function to check server status
  const checkServer = async () => {
    try {
      setLoading(true);
      setError(null);
      setServerInfo(null);
      setDatastoreStats(null);
      setTripletCount(null);
      setQueryResults(null);
      
      console.log(`Checking RDFox server`);
      console.log(`URL used: ${getRDFoxURL()}`);
      
      // Check server availability
      const status = await checkServerAvailability();
      console.log(`Check result:`, status);
      setServerStatus(status);
      
      if (status.available) {
        console.log(`Server available, retrieving additional information`);
        try {
          // Obtenir les informations du serveur
          const info = await getServerInfo();
          console.log(`Server information received:`, info);
          setServerInfo(info);
          
          // If datastores are available, select the first one
          if (status.datastores.length > 0) {
            const firstDatastore = status.datastores[0];
            console.log(`Selecting first datastore: ${firstDatastore}`);
            setSelectedDatastore(firstDatastore);
            
            // Compter les triplets dans le datastore sélectionné
            await countTriplets(firstDatastore);
            
            // Obtenir les statistiques du datastore
            await getStats(firstDatastore);
          } else {
            console.warn(`No datastores available`);
          }
        } catch (infoError) {
          console.error(`Erreur lors de la récupération des informations du serveur (${connectionType}):`, infoError);
          setError(`Erreur: ${infoError instanceof Error ? infoError.message : String(infoError)}`);
        }
      } else {
        console.error(`Failed to connect to RDFox server (${connectionType})`);
        setError(`Unable to connect to RDFox server (${connectionType}). Please verify that the server is online and accessible.`);
      }
    } catch (serverError) {
      console.error(`Erreur lors de la vérification du serveur (${connectionType}):`, serverError);
      setError(`Connection error: ${serverError instanceof Error ? serverError.message : String(serverError)}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour obtenir les statistiques d'un datastore
  const getStats = async (datastore: string) => {
    try {
      const stats = await getDatastoreStats(datastore);
      setDatastoreStats(stats);
    } catch (error) {
      console.error(`Erreur lors de la récupération des statistiques du datastore:`, error);
      setDatastoreStats(null);
    }
  };

  // Fonction pour compter les triplets dans un datastore
  const countTriplets = async (datastore: string) => {
    try {
      const countQuery = `
        SELECT (COUNT(*) AS ?count)
        WHERE {
          ?s ?p ?o
        }
      `;
      
      const results = await fetchRDFoxData(datastore, countQuery);
      
      if (results && results.results && results.results.bindings && results.results.bindings.length > 0) {
        const count = results.results.bindings[0].count?.value;
        setTripletCount(count ? parseInt(count, 10) : 0);
      } else {
        setTripletCount(0);
      }
    } catch (error) {
      console.error(`Erreur lors du comptage des triplets:`, error);
      setTripletCount(0);
    }
  };

  // Fonction pour exécuter une requête SPARQL
  const executeQuery = async () => {
    if (!selectedDatastore || !sparqlQuery.trim()) {
      setQueryError('Veuillez sélectionner un datastore et saisir une requête SPARQL');
      return;
    }
    
    try {
      setQueryLoading(true);
      setQueryError(null);
      
      const results = await fetchRDFoxData(selectedDatastore, sparqlQuery);
      setQueryResults(results);
    } catch (error) {
      console.error(`Erreur lors de l\'exécution de la requête:`, error);
      setQueryError(`Erreur: ${error instanceof Error ? error.message : String(error)}`);
      setQueryResults(null);
    } finally {
      setQueryLoading(false);
    }
  };

  // Fonction pour changer de datastore
  const handleDatastoreChange = async (value: string) => {
    setSelectedDatastore(value);
    
    if (value) {
      await countTriplets(value);
      await getStats(value);
    } else {
      setTripletCount(null);
      setDatastoreStats(null);
    }
  };
  

  // Formater les résultats pour l'affichage
  const formatResults = () => {
    if (!queryResults || !queryResults.results || !queryResults.results.bindings) {
      return <Paragraph>Aucun résultat disponible</Paragraph>;
    }

    const bindings = queryResults.results.bindings;
    if (bindings.length === 0) {
      return <Paragraph>Aucun résultat trouvé pour cette requête</Paragraph>;
    }

    // Préparer les colonnes pour la table Ant Design
    const columns = queryResults.head.vars.map((variable: string) => ({
      title: variable,
      dataIndex: variable,
      key: variable,
      render: (text: string) => text || '-'
    }));

    // Préparer les données pour la table Ant Design
    const dataSource = bindings.map((binding: any, index: number) => {
      const row: Record<string, string> = { key: index.toString() };
      queryResults.head.vars.forEach((variable: string) => {
        row[variable] = binding[variable] ? binding[variable].value : '';
      });
      return row;
    });

    return (
      <div>
        <Title level={4}>Résultats ({bindings.length} lignes)</Title>
        <Table 
          columns={columns} 
          dataSource={dataSource} 
          bordered 
          size="middle" 
          pagination={{ pageSize: 10 }}
        />
      </div>
    );
  };

  // Afficher les informations du serveur
  const renderServerInfo = () => {
    if (!serverInfo || !serverInfo.results || !serverInfo.results.bindings) {
      return <Paragraph>No information available about the server</Paragraph>;
    }

    const info: Record<string, string> = {};
    serverInfo.results.bindings.forEach((binding: any) => {
      if (binding.property && binding.value) {
        info[binding.property.value] = binding.value.value;
      }
    });

    // Préparer les colonnes pour la table Ant Design
    const columns = [
      {
        title: 'Property',
        dataIndex: 'property',
        key: 'property',
        width: '40%',
        render: (text: string) => <strong>{text}</strong>
      },
      {
        title: 'Valeur',
        dataIndex: 'value',
        key: 'value'
      }
    ];

    // Préparer les données pour la table Ant Design
    const dataSource = Object.entries(info).map(([key, value], index) => ({
      key: index.toString(),
      property: key,
      value: value
    }));

    return (
      <div>
        <Title level={4}>Server Information</Title>
        <Table 
          columns={columns} 
          dataSource={dataSource} 
          bordered 
          size="small" 
          pagination={false}
        />
      </div>
    );
  };

  // Fonction pour afficher les statistiques du datastore
  const renderDatastoreStats = () => {
    if (!datastoreStats || !datastoreStats.results || !datastoreStats.results.bindings || datastoreStats.results.bindings.length === 0) {
      return <Paragraph>No statistics available for this datastore</Paragraph>;
    }

    // Préparer les colonnes pour la table Ant Design
    const columns = [
      {
        title: 'Statistic',
        dataIndex: 'stat',
        key: 'stat',
        width: '40%',
        render: (text: string) => <strong>{text}</strong>
      },
      {
        title: 'Valeur',
        dataIndex: 'value',
        key: 'value'
      }
    ];

    // Préparer les données pour la table Ant Design
    const dataSource = datastoreStats.results.bindings.map((binding: any, index: number) => ({
      key: index.toString(),
      stat: binding.statistic?.value || '',
      value: binding.value?.value || ''
    }));

    return (
      <div>
        <Title level={4}>Statistiques du datastore</Title>
        <Table 
          columns={columns} 
          dataSource={dataSource} 
          bordered 
          size="small" 
          pagination={false}
        />
      </div>
    );
  };

  return (
    <Content style={{ padding: '24px' }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} md={12}>
          <Title level={2}>
            <CloudServerOutlined /> RDFox Explorer
          </Title>
        </Col>
        <Col xs={24} md={12} style={{ textAlign: 'right' }}>
          <Space>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={checkServer}
              type="primary"
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>
      
      <Divider />
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: '16px' }}>Checking connection to RDFox server ({connectionType})...</Paragraph>
        </div>
      ) : error ? (
        <Alert
          message={`Connection Error (${connectionType})`}
          description={error}
          type="error"
          showIcon
          action={
            <Button onClick={checkServer} type="primary" danger>
              Réessayer
            </Button>
          }
        />
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card title={
              <Space>
                <DatabaseOutlined />
                <span>Server Status (direct)</span>
              </Space>
            }>
              <div style={{ marginBottom: '16px' }}>
                <Text strong>URL: </Text>
                <Text code>{getRDFoxURL()}</Text>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Status: </Text>
                {serverStatus.available ? (
                  <Tag color="success">Connected</Tag>
                ) : (
                  <Tag color="error">Disconnected</Tag>
                )}
              </div>
              
              {serverStatus.available && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong>Available Datastores:</Text>
                    {serverStatus.datastores.length > 0 ? (
                      <div style={{ marginTop: '8px' }}>
                        {serverStatus.datastores.map((store, index) => (
                          <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: index < serverStatus.datastores.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                            <Text>{store}</Text>
                            {store === selectedDatastore && tripletCount !== null && (
                              <Tag color="blue">
                                {tripletCount} triplets
                              </Tag>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Paragraph type="secondary" style={{ marginTop: '8px' }}>No datastores available</Paragraph>
                    )}
                  </div>
                  
                  <Divider />
                  {renderServerInfo()}
                </>
              )}
            </Card>
            
            {serverStatus.available && selectedDatastore && datastoreStats && (
              <Card 
                title={
                  <Space>
                    <DatabaseOutlined />
                    <span>Datastore Statistics</span>
                  </Space>
                }
                style={{ marginTop: '16px' }}
              >
                {renderDatastoreStats()}
              </Card>
            )}
          </Col>
          
          <Col xs={24} md={16}>
            <Card title={
              <Space>
                <SearchOutlined />
                <span>SPARQL Query</span>
              </Space>
            }>
              {serverStatus.available ? (
                <>
                  <Form layout="vertical">
                    <Form.Item label="Select a datastore">
                      <Select
                        value={selectedDatastore}
                        onChange={handleDatastoreChange}
                        placeholder="Select a datastore"
                        style={{ width: '100%' }}
                        disabled={serverStatus.datastores.length === 0}
                      >
                        {serverStatus.datastores.map((store, index) => (
                          <Option key={index} value={store}>
                            {store} {tripletCount !== null && store === selectedDatastore ? `(${tripletCount} triplets)` : ''}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    
                    <Form.Item label="SPARQL Query">
                      <TextArea
                        rows={6}
                        value={sparqlQuery}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSparqlQuery(e.target.value)}
                        placeholder="Enter your SPARQL query here..."
                      />
                    </Form.Item>
                    
                    <Form.Item>
                      <Button
                        type="primary"
                        onClick={executeQuery}
                        disabled={queryLoading || !selectedDatastore || !sparqlQuery.trim()}
                        loading={queryLoading}
                        block
                      >
                        {queryLoading ? 'Executing...' : 'Execute Query'}
                      </Button>
                    </Form.Item>
                  </Form>
                  
                  {queryError && (
                    <Alert
                      message="Error"
                      description={queryError}
                      type="error"
                      showIcon
                      style={{ marginBottom: '16px' }}
                    />
                  )}
                  
                  <div>
                    {queryLoading ? (
                      <div style={{ textAlign: 'center', padding: '24px' }}>
                        <Spin tip="Loading results..." />
                      </div>
                    ) : (
                      queryResults && formatResults()
                    )}
                  </div>
                </>
              ) : (
                <Alert
                  message="Server not connected"
                  description="Please connect to the RDFox server first to execute SPARQL queries."
                  type="warning"
                  showIcon
                />
              )}
            </Card>
          </Col>
        </Row>
      )}
    </Content>
  );
};

export default RDFoxExplorer;
